import type { MouseEvent, ReactNode } from 'react'
import type { Invoice, InvoiceStatus, NextPageWithLayout, Plan } from '@/types'

import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { copyToClipboard, downloadCsv } from '@/lib/csv'
import { TODAY, formatDate } from '@/lib/date'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PageHeader } from '@/components/dashboard/page-header'
import { SectionCard } from '@/components/dashboard/section-card'
import { SegmentedControl } from '@/components/dashboard/segmented-control'
import { InvoiceStatusBadge } from '@/components/dashboard/status-badge'
import { EmptyState } from '@/components/dashboard/empty-state'
import { TablePagination } from '@/components/dashboard/table-pagination'
import { ConfirmDialog } from '@/components/dashboard/confirm-dialog'
import { PlanBadge } from '@/components/dashboard/plan-badge'
import { BarList } from '@/components/charts/bar-list'
import { PlanCard } from '@/components/billing/plan-card'
import { EditPriceDialog } from '@/components/billing/edit-price-dialog'
import { InvoiceDialog } from '@/components/billing/invoice-dialog'
import { InvoiceActionsMenu } from '@/components/billing/invoice-actions-menu'
import { PLAN_BREAKDOWN } from '@/data/customers'
import { INVOICES, getInvoiceStats, getPaymentMethodBreakdown, isOverdue } from '@/data/invoices'
import { PLANS, getPlan } from '@/data/plans'
import { INVOICE_STATUS_LABELS, PAYMENT_METHOD_LABELS } from '@/constants/dashboard'

import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    CircleCheck,
    CircleX,
    Clock,
    Download,
    Receipt,
    Search,
    SearchX
} from 'lucide-react'

import {
    formatNumber,
    formatPercent,
    formatRupiah,
    formatRupiahCompact
} from '@/lib/format'

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table'

type StatusFilter = 'all' | InvoiceStatus

type SortState = {
    key: 'issuedAt' | 'amount'
    direction: 'asc' | 'desc'
}

const STATUS_FILTERS: StatusFilter[] = ['all', 'paid', 'pending', 'failed', 'refunded']

const isStatusFilter = (value: unknown): value is StatusFilter => typeof value === 'string' && (STATUS_FILTERS as string[]).includes(value)

const compareText = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

const BillingPage: NextPageWithLayout = (): ReactNode => {
    const router = useRouter()
    const [invoices, setInvoices] = useState<Invoice[]>(INVOICES)
    const [plans, setPlans] = useState<Plan[]>(PLANS)
    const [status, setStatus] = useState<StatusFilter>('all')
    const [query, setQuery] = useState('')
    const [sort, setSort] = useState<SortState>({ key: 'issuedAt', direction: 'desc' })
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [viewing, setViewing] = useState<Invoice | null>(null)
    const [pricing, setPricing] = useState<Plan | null>(null)
    const [refunding, setRefunding] = useState<Invoice | null>(null)

    // ?status=failed comes from the notification menu and the search palette
    useEffect(() => {
        if (!router.isReady) return

        const value = router.query.status
        if (value === undefined) return

        if (isStatusFilter(value)) {
            setStatus(value)
            setPage(1)
        }

        router.replace('/langganan', undefined, { shallow: true })
    }, [router, router.isReady, router.query])

    const stats = useMemo(() => getInvoiceStats(invoices), [invoices])
    const methods = useMemo(() => getPaymentMethodBreakdown(invoices), [invoices])
    const mrrTotal = PLAN_BREAKDOWN.reduce((sum, item) => sum + item.mrr, 0)
    const term = query.trim().toLowerCase()

    const searched = useMemo(() => invoices.filter((invoice) => (
        !term
        || invoice.number.toLowerCase().includes(term)
        || invoice.company.toLowerCase().includes(term)
        || invoice.email.toLowerCase().includes(term)
    )), [invoices, term])

    const counts = useMemo(() => {
        const result: Record<StatusFilter, number> = { all: searched.length, paid: 0, pending: 0, failed: 0, refunded: 0 }
        searched.forEach((invoice) => {
            result[invoice.status] += 1
        })
        return result
    }, [searched])

    const sorted = useMemo(() => {
        const list = status === 'all' ? searched : searched.filter((invoice) => invoice.status === status)
        const factor = sort.direction === 'asc' ? 1 : -1

        return [...list].sort((a, b) => {
            const result = sort.key === 'amount' ? a.amount - b.amount : compareText(a.issuedAt, b.issuedAt)
            return result * factor || compareText(a.number, b.number) * factor
        })
    }, [searched, status, sort])

    const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize))
    const currentPage = Math.min(page, pageCount)
    const visible = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize)

    const followUps = useMemo(() => invoices
        .filter((invoice) => invoice.status === 'failed' || (invoice.status === 'pending' && isOverdue(invoice)))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 5), [invoices])

    const followUpTotal = useMemo(() => invoices
        .filter((invoice) => invoice.status === 'failed' || (invoice.status === 'pending' && isOverdue(invoice)))
        .length, [invoices])

    const updateInvoice = (id: string, changes: Partial<Invoice>): void => {
        setInvoices((list) => list.map((invoice) => (invoice.id === id ? { ...invoice, ...changes } : invoice)))
        setViewing((current) => (current && current.id === id ? { ...current, ...changes } : current))
    }

    const markPaid = (invoice: Invoice): void => {
        updateInvoice(invoice.id, { status: 'paid', paidAt: TODAY })
        toast.success(`${invoice.number} ditandai lunas`, { description: `${invoice.company}, ${formatRupiah(invoice.amount)}` })
    }

    const copyNumber = async (invoice: Invoice): Promise<void> => {
        const copied = await copyToClipboard(invoice.number)

        if (copied) toast.success('Nomor faktur disalin', { description: invoice.number })
        else toast.error('Nomor faktur tidak bisa disalin', { description: 'Izinkan akses clipboard di browser Anda lalu coba lagi.' })
    }

    const toggleSort = (key: SortState['key']): void => {
        setSort((current) => (current.key === key ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'desc' }))
        setPage(1)
    }

    const exportInvoices = (): void => {
        downloadCsv(
            `arunika-faktur-${TODAY}.csv`,
            ['Nomor', 'Pelanggan', 'Email', 'Paket', 'Jumlah (Rp)', 'Metode', 'Status', 'Diterbitkan', 'Jatuh tempo', 'Dibayar'],
            sorted.map((invoice) => [
                invoice.number,
                invoice.company,
                invoice.email,
                getPlan(invoice.plan).name,
                invoice.amount,
                PAYMENT_METHOD_LABELS[invoice.method],
                INVOICE_STATUS_LABELS[invoice.status],
                invoice.issuedAt,
                invoice.dueAt,
                invoice.paidAt ?? ''
            ])
        )

        toast.success(`${formatNumber(sorted.length)} faktur diekspor`, { description: 'File CSV siap dibuka di Excel atau Google Sheets.' })
    }

    const onRowClick = (event: MouseEvent<HTMLTableRowElement>, invoice: Invoice): void => {
        if (!event.currentTarget.contains(event.target as Node)) return
        setViewing(invoice)
    }

    const sortIcon = (key: SortState['key']) => (sort.key !== key ? ArrowUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown)
    const AmountIcon = sortIcon('amount')
    const DateIcon = sortIcon('issuedAt')

    const summary = [
        { label: 'Ditagih 30 hari terakhir', value: formatRupiahCompact(stats.billed), detail: `${formatNumber(stats.counts.all)} faktur`, icon: Receipt, tone: 'bg-muted text-muted-foreground' },
        { label: 'Sudah dibayar', value: formatRupiahCompact(stats.paid), detail: `${formatPercent(stats.billed === 0 ? 0 : (stats.paid / stats.billed) * 100)} dari total tagihan`, icon: CircleCheck, tone: 'bg-success/12 text-success' },
        { label: 'Menunggu pembayaran', value: formatRupiahCompact(stats.pending), detail: `${formatNumber(stats.counts.pending)} faktur`, icon: Clock, tone: 'bg-warning/14 text-warning' },
        { label: 'Pembayaran gagal', value: formatRupiahCompact(stats.failed), detail: `${formatNumber(stats.counts.failed)} faktur perlu dicoba lagi`, icon: CircleX, tone: 'bg-destructive/10 text-destructive' }
    ]

    return (
        <>
            <Seo title='Langganan' description='Paket langganan, faktur, dan pembayaran pelanggan Arunika.' />

            <div className='space-y-6'>
                <PageHeader
                    title='Langganan dan tagihan'
                    description='Pantau paket yang dijual, faktur yang terbit, dan pembayaran yang masih tertunda.'
                    actions={(
                        <Button variant='outline' size='lg' onClick={exportInvoices}>
                            <Download data-icon='inline-start' />
                            Ekspor faktur
                        </Button>
                    )}
                />

                <section aria-label='Ringkasan tagihan' className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
                    {summary.map((item) => {
                        const Icon = item.icon

                        return (
                            <div key={item.label} className='flex items-start gap-3 rounded-xl border bg-card p-5 shadow-[0_1px_2px_0_rgb(28_25_23/0.04)]'>
                                <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', item.tone)} aria-hidden='true'>
                                    <Icon className='size-[18px]' />
                                </span>
                                <div className='min-w-0'>
                                    <p className='text-sm text-muted-foreground'>{item.label}</p>
                                    <p className='mt-1 font-heading text-xl font-semibold text-foreground tabular-nums'>{item.value}</p>
                                    <p className='mt-0.5 text-xs text-muted-foreground'>{item.detail}</p>
                                </div>
                            </div>
                        )
                    })}
                </section>

                <section aria-labelledby='plans-heading' className='space-y-3'>
                    <div className='flex items-end justify-between gap-3'>
                        <div>
                            <h2 id='plans-heading' className='font-heading text-[15px] font-semibold text-foreground'>
                                Paket langganan
                            </h2>
                            <p className='mt-0.5 text-[13px] text-muted-foreground'>
                                Harga yang berlaku dan kontribusi tiap paket terhadap MRR.
                            </p>
                        </div>
                    </div>

                    <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
                        {plans.map((plan) => {
                            const breakdown = PLAN_BREAKDOWN.find((item) => item.plan === plan.id)

                            return (
                                <PlanCard
                                    key={plan.id}
                                    plan={plan}
                                    customers={breakdown?.customers ?? 0}
                                    mrr={breakdown?.mrr ?? 0}
                                    share={mrrTotal === 0 ? 0 : ((breakdown?.mrr ?? 0) / mrrTotal) * 100}
                                    onEditPrice={setPricing}
                                />
                            )
                        })}
                    </div>
                </section>

                <section aria-labelledby='invoices-heading' className='rounded-xl border bg-card shadow-[0_1px_2px_0_rgb(28_25_23/0.04)]'>
                    <div className='space-y-4 border-b p-4 sm:p-5'>
                        <div>
                            <h2 id='invoices-heading' className='font-heading text-[15px] font-semibold text-foreground'>
                                Faktur
                            </h2>
                            <p className='mt-0.5 text-[13px] text-muted-foreground'>
                                Semua faktur yang terbit dalam 30 hari terakhir.
                            </p>
                        </div>

                        <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
                            <div className='no-scrollbar -mx-1 overflow-x-auto px-1'>
                                <SegmentedControl
                                    label='Filter status faktur'
                                    value={status}
                                    onChange={(value) => {
                                        setStatus(value)
                                        setPage(1)
                                    }}
                                    options={STATUS_FILTERS.map((value) => ({
                                        value,
                                        label: (
                                            <>
                                                {value === 'all' ? 'Semua' : INVOICE_STATUS_LABELS[value]}
                                                <span className='text-xs text-muted-foreground tabular-nums'>{formatNumber(counts[value])}</span>
                                            </>
                                        )
                                    }))}
                                />
                            </div>

                            <div className='relative lg:w-80'>
                                <Search className='pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                                <Input
                                    type='search'
                                    value={query}
                                    onChange={(event) => {
                                        setQuery(event.target.value)
                                        setPage(1)
                                    }}
                                    placeholder='Cari nomor faktur atau pelanggan'
                                    aria-label='Cari faktur'
                                    className='h-9 pl-8'
                                />
                            </div>
                        </div>
                    </div>

                    {visible.length === 0
                        ? (
                            <EmptyState
                                icon={SearchX}
                                title='Tidak ada faktur yang cocok'
                                description='Ubah kata kunci atau pilih status lain.'
                                action={(
                                    <Button
                                        variant='outline'
                                        onClick={() => {
                                            setQuery('')
                                            setStatus('all')
                                        }}
                                    >
                                        Tampilkan semua faktur
                                    </Button>
                                )}
                            />
                        )
                        : (
                            <Table>
                                <TableHeader>
                                    <TableRow className='bg-muted/40 hover:bg-muted/40'>
                                        <TableHead className='pl-4 text-xs font-medium text-muted-foreground sm:pl-5'>Faktur</TableHead>
                                        <TableHead className='text-xs font-medium text-muted-foreground'>Pelanggan</TableHead>
                                        <TableHead className='hidden text-xs font-medium text-muted-foreground xl:table-cell'>Paket</TableHead>
                                        <TableHead className='text-right' aria-sort={sort.key === 'amount' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                            <button
                                                type='button'
                                                onClick={() => toggleSort('amount')}
                                                className={cn('-mx-1.5 inline-flex flex-row-reverse items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50', sort.key === 'amount' ? 'text-foreground' : 'text-muted-foreground')}
                                            >
                                                Jumlah
                                                <AmountIcon className='size-3.5' aria-hidden='true' />
                                            </button>
                                        </TableHead>
                                        <TableHead className='hidden text-xs font-medium text-muted-foreground lg:table-cell'>Metode</TableHead>
                                        <TableHead className='text-xs font-medium text-muted-foreground'>Status</TableHead>
                                        <TableHead className='hidden md:table-cell' aria-sort={sort.key === 'issuedAt' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                            <button
                                                type='button'
                                                onClick={() => toggleSort('issuedAt')}
                                                className={cn('-mx-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50', sort.key === 'issuedAt' ? 'text-foreground' : 'text-muted-foreground')}
                                            >
                                                Diterbitkan
                                                <DateIcon className='size-3.5' aria-hidden='true' />
                                            </button>
                                        </TableHead>
                                        <TableHead className='hidden text-xs font-medium text-muted-foreground lg:table-cell'>Jatuh tempo</TableHead>
                                        <TableHead className='w-12 pr-4 sm:pr-5'>
                                            <span className='sr-only'>Aksi</span>
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>

                                <TableBody>
                                    {visible.map((invoice) => (
                                        <TableRow key={invoice.id} className='cursor-pointer' onClick={(event) => onRowClick(event, invoice)}>
                                            <TableCell className='pl-4 sm:pl-5'>
                                                <button
                                                    type='button'
                                                    onClick={(event) => {
                                                        event.stopPropagation()
                                                        setViewing(invoice)
                                                    }}
                                                    className='rounded-sm font-mono text-xs font-medium text-foreground outline-none hover:underline focus-visible:ring-3 focus-visible:ring-ring/50'
                                                >
                                                    {invoice.number}
                                                </button>
                                                <p className='text-[11px] text-muted-foreground md:hidden'>{formatDate(invoice.issuedAt)}</p>
                                            </TableCell>
                                            <TableCell className='max-w-[240px]'>
                                                <p className='truncate font-medium text-foreground'>{invoice.company}</p>
                                                <p className='truncate text-xs text-muted-foreground'>{invoice.email}</p>
                                            </TableCell>
                                            <TableCell className='hidden xl:table-cell'>
                                                <PlanBadge plan={invoice.plan} />
                                            </TableCell>
                                            <TableCell className='text-right font-medium text-foreground tabular-nums'>
                                                {formatRupiah(invoice.amount)}
                                            </TableCell>
                                            <TableCell className='hidden text-muted-foreground lg:table-cell'>
                                                {PAYMENT_METHOD_LABELS[invoice.method]}
                                            </TableCell>
                                            <TableCell>
                                                <div className='flex flex-col items-start gap-0.5'>
                                                    <InvoiceStatusBadge status={invoice.status} />
                                                    {isOverdue(invoice) && <span className='text-[11px] font-medium text-destructive'>Lewat jatuh tempo</span>}
                                                </div>
                                            </TableCell>
                                            <TableCell className='hidden text-muted-foreground md:table-cell'>
                                                {formatDate(invoice.issuedAt)}
                                            </TableCell>
                                            <TableCell className='hidden text-muted-foreground lg:table-cell'>
                                                {formatDate(invoice.dueAt)}
                                            </TableCell>
                                            <TableCell className='pr-4 text-right sm:pr-5' onClick={(event) => event.stopPropagation()}>
                                                <InvoiceActionsMenu
                                                    invoice={invoice}
                                                    onView={setViewing}
                                                    onCopyNumber={copyNumber}
                                                    onMarkPaid={markPaid}
                                                    onRefund={setRefunding}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                    <div className='border-t px-4 py-3 sm:px-5'>
                        <TablePagination
                            page={currentPage}
                            pageSize={pageSize}
                            total={sorted.length}
                            noun='faktur'
                            onPageChange={setPage}
                            onPageSizeChange={(size) => {
                                setPageSize(size)
                                setPage(1)
                            }}
                        />
                    </div>
                </section>

                <div className='grid gap-4 lg:grid-cols-2'>
                    <SectionCard
                        title='Metode pembayaran'
                        description='Nilai faktur lunas menurut cara pelanggan membayar.'
                    >
                        <BarList
                            items={methods.map((item) => ({
                                key: item.method,
                                label: PAYMENT_METHOD_LABELS[item.method],
                                detail: `${formatNumber(item.count)} faktur`,
                                value: item.amount
                            }))}
                            formatValue={formatRupiahCompact}
                            showShare
                        />
                    </SectionCard>

                    <SectionCard
                        title='Perlu ditindaklanjuti'
                        description={`${formatNumber(followUpTotal)} faktur gagal dibayar atau lewat jatuh tempo. Lima terbesar:`}
                        contentClassName='px-0 pb-2'
                    >
                        {followUps.length === 0
                            ? (
                                <EmptyState
                                    icon={CircleCheck}
                                    title='Semua tagihan aman'
                                    description='Tidak ada faktur gagal atau terlambat saat ini.'
                                    className='py-10'
                                />
                            )
                            : (
                                <ul className='divide-y border-t'>
                                    {followUps.map((invoice) => (
                                        <li key={invoice.id} className='flex items-center gap-3 px-5 py-3'>
                                            <div className='min-w-0 flex-1'>
                                                <p className='truncate text-sm font-medium text-foreground'>{invoice.company}</p>
                                                <p className='font-mono text-[11px] text-muted-foreground'>
                                                    {invoice.number}, jatuh tempo {formatDate(invoice.dueAt)}
                                                </p>
                                            </div>
                                            <div className='hidden text-right sm:block'>
                                                <p className='text-sm font-semibold text-foreground tabular-nums'>{formatRupiah(invoice.amount)}</p>
                                                <InvoiceStatusBadge status={invoice.status} className='mt-1' />
                                            </div>
                                            <Button size='sm' variant='outline' onClick={() => markPaid(invoice)}>
                                                Tandai lunas
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                    </SectionCard>
                </div>
            </div>

            <InvoiceDialog
                invoice={viewing}
                onOpenChange={(open) => {
                    if (!open) setViewing(null)
                }}
                onCopyNumber={copyNumber}
                onMarkPaid={markPaid}
            />

            <EditPriceDialog
                plan={pricing}
                onOpenChange={(open) => {
                    if (!open) setPricing(null)
                }}
                onSave={(plan, prices) => {
                    setPlans((list) => list.map((item) => (item.id === plan.id ? { ...item, ...prices } : item)))
                    toast.success(`Harga ${plan.name} diperbarui`, {
                        description: `${formatRupiah(prices.price)} per bulan, berlaku untuk pelanggan baru.`
                    })
                }}
            />

            <ConfirmDialog
                open={refunding !== null}
                onOpenChange={(open) => {
                    if (!open) setRefunding(null)
                }}
                title='Kembalikan dana?'
                description={refunding ? `${formatRupiah(refunding.amount)} untuk faktur ${refunding.number} akan dikembalikan ke ${refunding.company}.` : ''}
                confirmLabel='Ya, kembalikan dana'
                destructive
                onConfirm={() => {
                    if (!refunding) return
                    updateInvoice(refunding.id, { status: 'refunded' })
                    toast.success(`Dana ${refunding.number} dikembalikan`)
                }}
            />
        </>
    )
}

export default BillingPage
